/**
 * feature/landing — the **S2 Overview**, in the §F4 splash card.
 *
 * The client (IFDC) returned the specification template on 7 Sep 2026 and it rewrote this
 * page from the inside out: `docs/client-specs/2026-09-07-overview-s2.md` is the contract.
 * The §F4 splash was a 50/50 card — a lede, four stat tiles, a status donut, a country bar
 * chart, a B.2.1 spark and a 320 px mini-map. S2 replaced that content with a map at 80 %
 * and an insight panel that recalculates against it.
 *
 * **Then the client reviewed S2 and asked for the frame back** (8 Sep 2026): *"make a splash
 * with it like it was before, keeping the current information on it."* S2 had also taken the
 * card to 100vw x 100vh, and that is what is undone — the card is min(1340px,96vw) x
 * min(830px,92vh) at radius 22 over the blurred backdrop again, and the 20/80 split is of the
 * card. Nothing in this file moved for it: the presentation is `base.css`, and the markup
 * below already had `#landCard`, `#landClose` and `#landFoot` because S2 never removed them.
 *
 * **What has never changed: the way a reader gets here.** It is still an overlay, not a route
 * — shown once per session behind the sign-in gate, reopened by the Overview nav, closed by ✕
 * or ESC, with the live app mounted underneath. The gate, the map explorer and the dashboard
 * are out of scope for this round as they were for S2.
 *
 * **The refresh rule is the page.** The client wrote it twice: *"All metrics should refresh in
 * real time whenever the user changes the map view, applies filters, or selects specific
 * countries, watersheds, or program areas."* So this component holds one `selection` and one
 * `view`, every panel value is derived from them (`./overview`), and nothing is fetched per
 * pan — the pathway counts arrive once per country as eleven rows and are summed here.
 * Precedence is explicit: an explicit pick wins, and only in its absence does the visible
 * extent drive the counts. A reader who selects a basin and then nudges the map keeps their
 * basin.
 *
 * **The filters are on the map** (user direction, 8 Sep 2026). S2 put them at the head of the
 * insight panel, on the argument that the panel is where the numbers they change are. The
 * client's line is the specification's own — "filters *on the overview map*" — so the bar now
 * floats at the top-left of `#landMapSlot`, over the thing it filters, and the panel begins
 * with the metric cards. The controls did not change: same three selects, same ids, same
 * Clear button, same `selection`. `OverviewMap` takes the bar as children rather than owning
 * it, because the state it writes is this component's.
 *
 * **Three panels, top to bottom**, which is the specification's own list and all of it:
 * the metric cards, "Activities by programme pathway", and "Progress Against Targets".
 * The §F4 donut, country bars and B.2.1 spark are gone with the layout that held them —
 * a 20 % column cannot carry six charts, and the client enumerated the three it wants.
 *
 * ---
 *
 * **R1 — the client's revision round on this page** (8 Sep 2026,
 * `docs/client-specs/2026-09-08-overview-s2-revisions.md`). Nine points from IFDC's review of
 * the built page plus a tenth from the coordinator. What they changed here:
 *
 * * **(1)** the header lost its narrative paragraph — the sign-in page already carries it —
 *   and the mark grew to 64 px;
 * * **(4)** the pathway chart lost its colour legend, which restated three labels and three
 *   numbers the bars already carry;
 * * **(8)** the bottom strip stopped rendering `/audit/ticker` and now rotates insights
 *   generated by `tickerInsights` from the memos below. No fetch, no backend change;
 * * **(9)** one hero button became two equal actions, and the Dashboard is gated through
 *   `programmeInfoAllowed` — the S3 Indicators icon's own call, not a second rule;
 * * **(10)** a filter now removes the geography it excludes from the map, not just from the
 *   counts. `pathwayScope` is the new half of that: a programme pathway has no geometry, so
 *   its "area" is the basins the server reports activity in for it, and that set is threaded
 *   into every count *and* into the map so the two cannot disagree.
 *
 * Items 2, 3, 5, 6 and 7 are the map component's and the stylesheet's; the cards, the pathway
 * chart's own arithmetic, the targets section and the three filters are untouched.
 *
 * ---
 *
 * **R2 — the client's second round** (8 Sep 2026, evening; addendum in the same spec file).
 * Two of its three items are this file's, and both are subtractions:
 *
 * * the two target charts lost their *Target / Achieved* colour key and their "targets pending
 *   from IFDC" note, and the page lost the "synced hourly from MELS" footnote —
 *   `overview.targets.pending` and `splash.liveness` are gone from both locale files. The
 *   pending *logic* is untouched: when IFDC supplies figures the target bars simply appear.
 *   **R1 item 4 kept those legends deliberately and the client has overruled that**, which is
 *   recorded at the chart and in the spec;
 * * the four headings the client named are a step up in size and weight (`base.css`), with the
 *   hierarchy held: page title > section head > chart sub-head > label.
 *
 * The third item is the map's call-outs (`OverviewMap`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BarsH, TargetRows, type TargetRowDatum } from '../../components/charts';
import { BARSH_BAR_GAP, BARSH_ROW_GAP } from '../../components/charts/BarsH';
import {
  ANNUAL,
  fetchConfig,
  fetchIndicators,
  fetchPathways,
  fetchProgrammeSeries,
  fetchTargets,
  fetchUnits,
  queryKeys,
  type Indicator,
  type IndicatorsKey,
  type PathwaysKey,
  type PathwaysResponse,
  type SeriesResponse,
  type TargetsKey,
  type TargetsResponse,
  type UnitsFC,
} from '../../lib/api';
import { EMDASH, fmtNum } from '../../lib/format';
import { Icon } from '../../lib/icons';
import { COUNTRY_NAMES, selectYear, useAppStore, type Country } from '../../lib/state';
import { programmeInfoAllowed } from '../sidebar/themes';
import { OverviewMap, type FitTarget } from './OverviewMap';
import {
  activityExtremes,
  hasAnyFilter,
  hasSelection,
  metricCounts,
  pathwayBars,
  pathwayColors,
  pathwayScope,
  targetRows,
  targetTipKey,
  tickerInsights,
  unitsInView,
  NO_SELECTION,
  PROGRAMME_COUNTRIES,
  type Bbox,
  type OverviewSelection,
  type ProgrammeCountry,
} from './overview';

/**
 * One bar thickness for every chart on this panel (client, 2026-09-12).
 *
 * *"the sizes of the bars on our charts on the overview are not the same, they want such that
 * the bar sizes under Activities by programme pathway, C.2.1 … B.2.1 … are all the same."*
 *
 * Matching `rowH` across the charts — the 09-12 morning attempt — could not do it, and the
 * measurement says why: the pathway draws ONE series per row and the target charts draw TWO,
 * so a shared 30px row gave 23px bars on one chart and 10.5px on the others. Thickness is what
 * the client is looking at, so thickness is what is fixed here and the row height is derived
 * from it per chart. `BarsH` lays a band out as `rowH − ROW_GAP`, then splits it between the
 * series with `BAR_GAP` between them; this inverts that arithmetic rather than restating it,
 * so the two cannot drift apart the next time a constant moves.
 */
const OV_BAR_H = 18;
const ovRowH = (series: number) => OV_BAR_H * series + BARSH_BAR_GAP * (series - 1) + BARSH_ROW_GAP;


/** The consortium credit along the bottom of the backdrop — the mockup's, verbatim. */
const PARTNERS = 'IFDC · SNV · WUR · ICRAF · ISRIC · IITA';

/**
 * The two indicators S2 names: "Hectares targeted vs achieved — derived from C.2.1" and
 * "Producers reached vs target — derived from B.2.1". Ids rather than codes, because ids are
 * what every route takes.
 */
const TARGET_INDICATORS = ['c21', 'b21'] as const;
/** The indicator whose `default_year` decides which year this page opens on. */
const HEADLINE_INDICATOR = 'b21';

/** Target bars over achieved bars, in the mockup's own two colours (blue over green). */
const TARGET_COLORS = { target: '#2B6CB0', achieved: '#2E7D32' };

/** The four programme countries the country filter offers, in §G8's fixed order, plus the
 *  "all" entry that is a filter value rather than a place. One list, shared with the target
 *  chart's rows, so the filter and the chart can never offer different countries. */
const COUNTRY_FILTER: Country[] = ['ALL', ...PROGRAMME_COUNTRIES];

/**
 * The trajectory's last year — IFDC's end-of-programme ambition, and the year each country
 * row's Target bar is read at.
 *
 * A constant only as the fallback: the programme-scope response the panel already loads
 * carries `eop.year`, and that is preferred, so the day IFDC extends or shortens the
 * trajectory the chart follows the delivery instead of this line.
 */
const EOP_YEAR = 2033;

export function SplashLanding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const splashOpen = useAppStore((s) => s.splashOpen);
  /** R1: the sign-in gate is the session's first page, and this waits behind it. */
  const gateOpen = useAppStore((s) => s.gateOpen);
  const open = splashOpen && !gateOpen;
  const setSplashOpen = useAppStore((s) => s.setSplashOpen);
  const setCountry = useAppStore((s) => s.setCountry);
  /** §I2 mirror — the Dashboard action's gate, below. */
  const role = useAppStore((s) => s.role);
  /**
   * The year the target comparison reads. This page has no time bar, so it takes the app's
   * own year, which moves whenever anything else in the app moves it — the smallest honest
   * wiring of "year selection drives the target" on a page with no year control of its own.
   *
   * **It opens on the latest substantially reported year, not the last programme year.**
   * The store's cold-load year is 2026, and until 2026-09-15 this page read it as-is, so the
   * client opened on a year MELS has three rows for and saw IFDC's 2026 commitment — while
   * saying, correctly, that "2025" is the current picture and its actuals are its targets.
   * The catalogue already answers which year *is* the picture (`default_year`, §D3: the
   * newest year holding at least half the best year's rows — 2025 today), and the store's
   * own `applyDefaultYear` adopts it once and never over a reader's choice (`yearPinned`).
   * The explorer applies it when an indicator is mapped; this page applies it for its
   * headline indicator, B.2.1, so the Overview and the explorer agree on what "now" is.
   */
  const year = useAppStore(selectYear);
  const applyDefaultYear = useAppStore((s) => s.applyDefaultYear);

  /**
   * The page's own selection, deliberately **not** the global `country` in the store.
   *
   * S2's filters drive this page and nothing else; writing them through to the app-wide
   * country would re-fit the Map Explorer mounted underneath and re-scope the sidebar the
   * reader has not looked at yet. The store is written only on the way out — clicking
   * through to the map adopts the country, as §F4's contract always did.
   */
  const [selection, setSelection] = useState<OverviewSelection>(NO_SELECTION);
  const [view, setView] = useState<Bbox | null>(null);
  const [geometry, setGeometry] = useState<{ watersheds?: UnitsFC; partnerships?: UnitsFC }>({});
  const [activity, setActivity] = useState<{ unit_id: string; count: number }[]>([]);
  /** Activities in no basin at all — the map's own payload says so, and a ticker line uses it. */
  const [unassigned, setUnassigned] = useState(0);
  const [fit, setFit] = useState<{ target: FitTarget; nonce: number } | null>(null);

  const close = useCallback(() => setSplashOpen(false), [setSplashOpen]);

  /** ESC closes — the pattern `DrawAoi`/`Measure`/`SearchBox` already use. */
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const goto = useCallback(
    (path: string) => {
      close();
      navigate(path);
    },
    [close, navigate],
  );

  const { data: pathways } = useQuery<PathwaysResponse, Error, PathwaysResponse, PathwaysKey>({
    queryKey: queryKeys.pathways(selection.country),
    queryFn: fetchPathways,
    staleTime: 60_000,
    enabled: open,
  });
  const { data: catalogue } = useQuery<Indicator[], Error, Indicator[], IndicatorsKey>({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
    enabled: open,
  });
  useEffect(() => {
    applyDefaultYear(catalogue?.find((row) => row.id === HEADLINE_INDICATOR)?.default_year);
  }, [catalogue, applyDefaultYear]);
  /** The country names the target chart's rows are labelled with. */
  const { data: countryUnits } = useQuery<UnitsFC>({
    queryKey: queryKeys.units('admin', 0, 'ALL', 'z5'),
    queryFn: () => fetchUnits('admin', 0, 'ALL', 'z5'),
    enabled: open,
  });
  /** `features.open_access` — the flag the Dashboard action is gated through. */
  const { data: config } = useQuery({
    queryKey: queryKeys.config(),
    queryFn: fetchConfig,
    enabled: open,
  });

  const requestFit = useCallback((target: FitTarget) => {
    // A nonce rather than the target alone: clicking the Watersheds card twice must refit
    // twice, and two structurally identical requests would otherwise be one.
    setFit((previous) => ({ target, nonce: (previous?.nonce ?? 0) + 1 }));
  }, []);

  const onView = useCallback((next: Bbox, _zoom: number) => setView(next), []);
  const onData = useCallback(
    (next: { watersheds?: UnitsFC; partnerships?: UnitsFC }) => setGeometry(next),
    [],
  );
  const onActivity = useCallback(
    (rows: { unit_id: string; count: number }[], unassignedRows: number) => {
      setActivity(rows);
      setUnassigned(unassignedRows);
    },
    [],
  );

  const pickWatershed = useCallback((unitId: string, country: Country | null) => {
    setSelection((current) =>
      // Clicking the selected basin again clears it — the only way back to the programme
      // view without hunting for the filter, and the gesture a reader tries first.
      current.watershed === unitId
        ? { ...current, watershed: null }
        : { ...current, watershed: unitId, country: country ?? current.country },
    );
  }, []);
  const pickCountry = useCallback((country: Country) => {
    setSelection((current) => ({
      ...current,
      country: current.country === country ? 'ALL' : country,
      // A different country cannot keep a watershed that is not in it.
      watershed: null,
    }));
  }, []);

  /* ------------------------------------------------------- the derived numbers */

  /**
   * R1 item 10: the programme-pathway filter's own geography — the basins the server reports
   * activity in for that pathway. `null` when no pathway is picked, which is "do not scope".
   * Threaded into every count *and* into the map, so the two cannot disagree about which
   * basins a pathway touches.
   */
  const pathScope = useMemo(
    () => pathwayScope(pathways, selection.pathway),
    [pathways, selection.pathway],
  );
  const watershedsInView = useMemo(
    () => unitsInView(geometry.watersheds, view, selection, pathScope),
    [geometry.watersheds, view, selection, pathScope],
  );
  const partnershipsInView = useMemo(
    () => unitsInView(geometry.partnerships, view, selection, pathScope),
    [geometry.partnerships, view, selection, pathScope],
  );
  const counts = useMemo(
    () => metricCounts(watershedsInView, partnershipsInView, selection),
    [watershedsInView, partnershipsInView, selection],
  );
  const scopedUnitIds = useMemo(
    () =>
      (selection.watershed
        ? watershedsInView.filter((w) => w.id === selection.watershed)
        : watershedsInView
      ).map((w) => w.id),
    [watershedsInView, selection.watershed],
  );
  const bars = useMemo(
    () => pathwayBars(pathways, scopedUnitIds, t('overview.pathway.series'), selection.pathway),
    [pathways, scopedUnitIds, selection.pathway, t],
  );
  const barColors = useMemo(
    () =>
      selection.pathway
        ? pathwayColors(pathways).filter(
            (_, i) => pathways?.pathways[i]?.id === selection.pathway,
          )
        : pathwayColors(pathways),
    [pathways, selection.pathway],
  );
  const countryNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const feature of countryUnits?.features ?? []) {
      const props = feature.properties;
      if (props?.id) map.set(props.id, props.name);
    }
    return map;
  }, [countryUnits]);

  /**
   * The two extremes, computed here as well as map-side.
   *
   * The same pure function over the same rows, so the badge on the polygon and the line in
   * the panel cannot disagree — which is exactly what two independent "find the busiest one"
   * loops would eventually do.
   */
  /**
   * The basins the **filter** leaves standing, ignoring the extent — the same set the map now
   * draws (`OverviewMap` applies `inSelection` to its own rows). The extremes are computed
   * over these rather than over the whole registry so that the badge on the map, the line in
   * the panel and the ticker all name a basin the reader can actually see.
   */
  const selectedIds = useMemo(
    () => new Set(unitsInView(geometry.watersheds, null, selection, pathScope).map((w) => w.id)),
    [geometry.watersheds, selection, pathScope],
  );
  const scopedActivity = useMemo(
    () => activity.filter((row) => selectedIds.has(row.unit_id)),
    [activity, selectedIds],
  );
  const extremes = useMemo(() => activityExtremes(scopedActivity), [scopedActivity]);
  const watershedName = useCallback(
    (unitId: string) =>
      unitsInView(geometry.watersheds, null, NO_SELECTION).find((w) => w.id === unitId)?.name ??
      unitId,
    [geometry.watersheds],
  );

  /**
   * R1 item 8: the bottom strip rotates **data insights**, generated here from the memos
   * above and fetched from nowhere. Every line is a number that is also on this panel, which
   * is the client's own condition — *"such insights should already exist at the Insights
   * panel for follow-up"*.
   *
   * **No target statement is among them** — client directive, 2026-09-09, after the 13:00
   * review, reversing the same day's Rule 6. What rotates is activities, watersheds and
   * pathways; the charts below keep their bars and their reference line and say nothing in
   * prose. `tickerInsights` takes no target input at all now, so this cannot drift back.
   */
  const insights = useMemo(
    () =>
      tickerInsights({
        counts,
        extremes,
        name: watershedName,
        activity: scopedActivity,
        bars,
        unassigned,
        fmt: (value) => fmtNum(value, 0),
      }),
    [counts, extremes, watershedName, scopedActivity, bars, unassigned],
  );

  if (!open) return null;

  const scoped = hasSelection(selection);
  /** The Clear button's own test — a pathway is clearable even though it is not a geography. */
  const filtered = hasAnyFilter(selection);
  /**
   * Is the Dashboard action open? The S3 Indicators icon's own test, applied to the S2
   * bottom bar: the dashboard is *"SV Programme Information"* in the client's words and the
   * Indicators catalogue is the other thing the server marks `requires_signin`, so they are
   * one gate rather than two. `openAccess` short-circuits it while the read surface is open.
   */
  const dashboardLocked = !programmeInfoAllowed(config, role);
  const selectedName =
    watershedsInView.find((w) => w.id === selection.watershed)?.name ??
    (selection.country !== 'ALL'
      ? t(`country.${selection.country}`, COUNTRY_NAMES[selection.country] ?? selection.country)
      : '');

  /** S2 §1: three cards, each of which refits the map to the feature type it counts. */
  const cards: { key: string; value: number; fit: FitTarget }[] = [
    { key: 'countries', value: counts.countries, fit: { kind: 'countries' } },
    { key: 'watersheds', value: counts.watersheds, fit: { kind: 'watersheds' } },
    { key: 'partnerships', value: counts.partnerships, fit: { kind: 'partnerships' } },
  ];

  return (
    <div
      id="landOv"
      className="land-splash"
      role="dialog"
      aria-modal="true"
      aria-labelledby="landTitle"
      data-testid="splash"
    >
      <div id="landCard">
        <div id="homeContent" data-testid="overview-panel">
          {/*
            R1 item 1: **logo and title, and nothing else.** The header carried a two-line
            narrative about where the programme works; the client's note is that the same
            narrative is already the sign-in page's own copy — *"SV narrative already captured
            on the sign-in page hence not needed here"* — so it is gone, and the mark it sat
            beside is drawn at 64 px instead of 44 so it is legible rather than decorative.
          */}
          <div className="hp-head">
            <span className="hp-logo">
              <img src="/logo.png" alt="" width={112} height={112} />
            </span>
            <h2 id="landTitle">{t('splash.title')}</h2>
          </div>

          {/* S2 §1: the three dynamic metric cards. "Light 3D interactive features,
              especially when moused over" — the lift is in `base.css`. */}
          <div className="ov-cards" data-testid="overview-cards">
            {cards.map((card) => (
              <button
                type="button"
                key={card.key}
                className="ov-card"
                data-card={card.key}
                data-value={card.value}
                onClick={() => requestFit(card.fit)}
                title={t(`overview.card.${card.key}.hint`)}
              >
                <b>{fmtNum(card.value, 0)}</b>
                <span>{t(`overview.card.${card.key}.label`)}</span>
              </button>
            ))}
          </div>
          <p className="hp-note ov-scope" data-testid="overview-scope">
            {scoped
              ? t('overview.scope.selection', { name: selectedName })
              : t('overview.scope.extent')}
          </p>

          {/*
            2026-09-12, client: *"take this text off: Most activity · Kou / Least activity ·
            Nakambe"*, and the same instruction took the call-outs off the map. S2 §3's
            highlight survives as the ring the two basins still carry; what is withdrawn is
            naming them in words. `extremes` is still computed and still styles those rings.
          */}
          {/* S2 §2: activities by programme pathway. */}
          <h4>{t('overview.pathway.title')}</h4>
          <div className="hp-chart" data-testid="overview-pathway">
            <BarsH
              labels={bars.labels}
              // One series with per-row colours, not three one-row series: three series
              // would each get their own axis scale and the bars would stop being
              // comparable, which is the whole job of this chart.
              series={bars.series.map((s) => ({ ...s, rowColors: barColors }))}
              rowH={ovRowH(1)}
              // A pathway name is a phrase, not a country: `Enabling Environment` — and in
              // French `Environnement favorable` — needs a wider gutter and a smaller face
              // than the default, or it is truncated to nothing worth reading.
              labelGutter={112}
              labelSize={8}
              fmt={0}
              ariaLabel={t('overview.pathway.title')}
            />
            {/*
              R1 item 4: the colour legend is gone. *"Appears repetitive. Bar chart colors
              already indicate legend. Remove."* — and it was: every row already carries the
              pathway's name as its axis label and its count at the end of the bar, in the
              pathway's own colour, so the block under it restated three labels and three
              numbers that were six inches away. The per-bar colours stay (`barColors`); it is
              the key to them that was redundant.
            */}
            {/* The honest denominator. 86 % of the registry carries no category upstream,
                and three bars over 219 activities captioned "activities" would be wrong by
                a factor of eight. */}
            <div className="hp-note" data-testid="overview-uncategorised">
              {/* Two sentences, each pluralised on its own count — one `count` cannot
                  inflect both nouns (NOTES #677). */}
              {t('overview.pathway.noteCategorised', {
                count: bars.categorised,
                value: fmtNum(bars.categorised, 0),
              })}{' '}
              {t('overview.pathway.noteUncategorised', {
                count: bars.uncategorised,
                value: fmtNum(bars.uncategorised, 0),
              })}
            </div>
          </div>

          {/* S2 §3: Progress Against Targets, one chart per contractual indicator. */}
          <h4>{t('overview.targets.title')}</h4>
          {TARGET_INDICATORS.map((id) => (
            <TargetPanel
              key={id}
              indicatorId={id}
              catalogue={catalogue}
              country={selection.country}
              year={year}
              names={countryNames}
              onPick={(unitId) => requestFit({ kind: 'unit', id: unitId })}
            />
          ))}

          {/*
            **No target-derived statements here** — client directive, 2026-09-09, after the
            13:00 review, taking back what the same day's specification (Rule 6) had asked
            for: *"remove ALL target-derived insight statements from the insight panel's list
            AND from the rotating bottom ticker"*.

            The charts above keep their real Target and Achieved bars and their programme
            reference line — the numbers stay, the sentences about them go. The generator is
            kept dormant in `overview.ts` for the dashboard task; its strings were pruned
            from both locale bundles with the list they filled.
          */}

          {/*
            R1 item 9: **two equally weighted actions**, at the client's own words — *"Could we
            split this bar into 2 sections — 'View the Map Explorer' and 'View the Dashboard'.
            Clicking each opens the content."* Both close the splash and navigate; "Take the
            tour" stays, secondary, under them.

            The Dashboard is gated exactly as the S3 Indicators icon is — `themeAllowed` with
            `openAccess(config)` — and deliberately through the *same* helper rather than a
            second rule that means the same thing today. Under the 2026-09-08 OPEN_ACCESS
            directive the flag is on and everybody sees both actions; when enforcement returns,
            a guest gets the Dashboard **shown and disabled with a lock**, which is what the
            Indicators icon does, so the two surfaces cannot drift into disagreeing about what
            a locked thing looks like.
          */}
          {/*
            2026-09-12, client: *"make these buttons … such that users would not have to scroll
            all the way down to access them, so it should be such that they are static so even
            when a user scrolls they are still visible … we could create a kind of section down
            where they are so scrolling makes the side go behind that section."*

            A sticky footer inside the panel's own scroller, not a fixed overlay: `position:
            sticky` keeps the dock in the document, so it reserves its own height and the last
            chart can still be scrolled clear of it. A `position:fixed` bar would have floated
            over the final rows and hidden them, which is the failure this instruction is about.
          */}
          <div className="ov-dock" data-testid="overview-dock">
            <div className="ov-actions" data-testid="overview-actions">
              <button
                type="button"
                className="btn primary hp-hero-btn"
                id="hpExplore"
                onClick={() => {
                  // The one place this page writes the global filter: a reader leaving for the
                  // Map Explorer takes the country they were looking at with them (§F4).
                  if (selection.country !== 'ALL') setCountry(selection.country);
                  goto('/');
                }}
              >
                <Icon name="map" size={19} />
                {t('splash.cta.explore')}
              </button>
              <button
                type="button"
                className={`btn primary hp-hero-btn${dashboardLocked ? ' locked' : ''}`}
                id="hpDash"
                disabled={dashboardLocked}
                aria-disabled={dashboardLocked ? true : undefined}
                title={dashboardLocked ? t('splash.cta.dashboardLocked') : undefined}
                onClick={dashboardLocked ? undefined : () => goto('/dashboard')}
              >
                <Icon name={dashboardLocked ? 'lock' : 'chart'} size={19} />
                {dashboardLocked ? t('splash.cta.dashboardLocked') : t('splash.cta.dashboard')}
              </button>
            </div>
            <div className="hp-ctas">
              <button type="button" className="btn ghost" id="hpTour" onClick={close}>
                <Icon name="info" size={14} />
                {t('splash.cta.tour')}
              </button>
            </div>
          </div>
          {/*
            The liveness footnote — *"Activity locations and B.2.1 figures are synced hourly
            from MELS; dataset counts are live from the catalogue"* — is gone at the client's
            instruction (2026-09-08, R2 item 2), along with the `splash.liveness` string in
            both locales. It described the pipeline rather than the programme, which is the
            same objection R1 item 8 made about the ticker; this is the last of it on the page.
          */}
        </div>

        <OverviewMap
          selection={selection}
          onPickWatershed={pickWatershed}
          onPickCountry={pickCountry}
          onView={onView}
          onData={onData}
          onActivity={onActivity}
          fit={fit}
          pathwayScope={pathScope}
          insights={insights}
        >
          {/*
            S2 §2: "Provide filters on the overview map (country, watershed, programme area)
            to drive this." — and, from 8 Sep 2026, *on* the map literally: the bar floats at
            the top-left of `#landMapSlot` rather than sitting above the metric cards.

            The controls are unchanged, ids included: the same three selects writing the same
            `selection`, the same Clear button appearing only once something is picked. Only
            the bar's home moved, so the refresh rule, the partnership scoping and every chart
            recalculation are the code they already were — see `./overview`.

            It is passed to `OverviewMap` as children because `selection` belongs here, beside
            the eight `useMemo`s that read it; the map component paints the bar and knows
            nothing about what it does.
          */}
          <div className="ov-filters glass" data-testid="overview-filters">
            <label>
              <span>{t('overview.filter.country')}</span>
              <select
                id="ovCountry"
                value={selection.country}
                onChange={(e) =>
                  setSelection((s) => ({
                    ...s,
                    country: e.target.value as Country,
                    watershed: null,
                  }))
                }
              >
                {COUNTRY_FILTER.map((code) => (
                  <option key={code} value={code}>
                    {code === 'ALL'
                      ? t('overview.filter.allCountries')
                      : t(`country.${code}`, COUNTRY_NAMES[code] ?? code)}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t('overview.filter.watershed')}</span>
              <select
                id="ovWatershed"
                value={selection.watershed ?? ''}
                onChange={(e) =>
                  setSelection((s) => ({ ...s, watershed: e.target.value || null }))
                }
              >
                <option value="">{t('overview.filter.allWatersheds')}</option>
                {unitsInView(geometry.watersheds, null, {
                  ...selection,
                  watershed: null,
                }).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{t('overview.filter.pathway')}</span>
              <select
                id="ovPathway"
                value={selection.pathway ?? ''}
                onChange={(e) => setSelection((s) => ({ ...s, pathway: e.target.value || null }))}
              >
                <option value="">{t('overview.filter.allPathways')}</option>
                {(pathways?.pathways ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </label>
            {filtered && (
              <button
                type="button"
                id="ovClear"
                className="btn ghost ov-clear"
                onClick={() => setSelection(NO_SELECTION)}
              >
                {t('overview.filter.clear')}
              </button>
            )}
          </div>
        </OverviewMap>

        <button
          type="button"
          id="landClose"
          title={t('splash.close')}
          aria-label={t('splash.close')}
          onClick={close}
        >
          <Icon name="x" size={16} />
        </button>
      </div>
      <div id="landFoot">{`${PARTNERS} — ${t('splash.programme')}`}</div>
    </div>
  );
}

/**
 * The three numbers one "Progress Against Targets" chart is built from, fetched once.
 *
 * Called from the page *and* from each panel: TanStack serves the second caller from cache
 * on the same key, so the insight generator upstairs and the chart downstairs are reading
 * the same request rather than two that could disagree.
 *
 * **Cumulative, at the selected year.** The client's specification is explicit that every
 * comparison is cumulative achieved against `cumulative_target`, and that the 2025 actuals
 * are already inside the trajectory — so the achieved half is `?mode=cumulative`, and asking
 * for the annual reading here would compare a year's step against a running total.
 */
function useTargetData(indicatorId: string, year: number, country: Country) {
  const { data: targets } = useQuery<TargetsResponse, Error, TargetsResponse, TargetsKey>({
    queryKey: queryKeys.targets(indicatorId, 0, year, country),
    queryFn: fetchTargets,
  });
  return { targets };
}

interface TargetPanelProps {
  indicatorId: string;
  catalogue: Indicator[] | undefined;
  country: Country;
  year: number;
  names: Map<string, string>;
  onPick: (unitId: string) => void;
}

/**
 * One indicator's "Progress against targets": **one row per country, that country's
 * programme long-term target as one bar beside a stack of its yearly actuals** — the client's
 * shape since 4 Oct 2026, replacing *both* of the charts that stood here the day before (the
 * 2026-09-15 Target-beside-Achieved `BarsH` rows and the 2026-10-04 morning's
 * programme-against-yearly-targets columns).
 *
 * Every number is already served; this panel adds no endpoint and no request-time arithmetic:
 *
 * * **the target** is `/indicators/{id}/targets?year={eop}&country={ISO3}`, whose
 *   `target.cumulative_target` is IFDC's own country allocation where it made one (B.2.1 and
 *   C.2.1, 6 Oct 2026: `allocated: true`, the bar's tooltip says "IFDC country target"), and
 *   elsewhere the programme commitment apportioned to that country by its share of the
 *   cumulative actuals (`source: "apportioned"`). Both rules stay in the one place that owns
 *   them — the server — and the four B.2.1 bars add to 1,500,000 because IFDC's do;
 * * **the stack** is `/indicators/{id}/series?scope=programme&country={ISO3}&mode=annual`, one
 *   segment per year that reported a finite number. Annual, not cumulative: a stack of
 *   running totals would count 2025 again inside 2026.
 *
 * `useQueries` because the row count is the filter's: four countries when nothing is picked,
 * and exactly the picked one otherwise — the chart narrows to one row rather than disappearing.
 *
 * **The apportioning is still said in words**, on the panel head's `title`, for the reader who
 * hovers it: a bar labelled with a country's name is a bar that has to say it was divided.
 * Not on an allocated indicator: nothing there was divided by the portal.
 */
function TargetPanel({ indicatorId, catalogue, country, year, names, onPick }: TargetPanelProps) {
  const { t } = useTranslation();
  const indicator = catalogue?.find((row) => row.id === indicatorId);
  const unit = indicator?.unit ?? '';
  /* The programme-scope answer this panel has always loaded. It is read for two things now:
     the end-of-programme year, and whether the server calls its country figures apportioned. */
  const { targets } = useTargetData(indicatorId, year, country);
  const eopYear = targets?.eop?.year ?? EOP_YEAR;

  const countries = useMemo<readonly ProgrammeCountry[]>(
    () => (country === 'ALL' ? PROGRAMME_COUNTRIES : [country]),
    [country],
  );

  const eopQueries = useQueries({
    queries: countries.map((iso3) => ({
      queryKey: queryKeys.targets(indicatorId, 0, eopYear, iso3),
      queryFn: fetchTargets,
    })),
  });
  const seriesQueries = useQueries({
    queries: countries.map((iso3) => ({
      queryKey: queryKeys.programmeSeries(indicatorId, iso3, ANNUAL),
      queryFn: () => fetchProgrammeSeries(indicatorId, iso3, ANNUAL),
    })),
  });

  const eop = eopQueries.map((q) => q.data as TargetsResponse | undefined);
  const series = seriesQueries.map((q) => q.data as SeriesResponse | undefined);
  /* The one place the word "apportioned" is earned: the server's own flag on the country
     answers, not an inference from the numbers. */
  const allocated = eop.some((row) => row?.allocated);
  const apportioned = !allocated && eop.some((row) => row?.apportioned);

  const data = targetRows(
    countries,
    names,
    new Map(countries.map((iso3, i) => [iso3, eop[i]?.target?.cumulative_target ?? null])),
    new Map(countries.map((iso3, i) => [iso3, series[i]])),
    new Map(countries.map((iso3, i) => [iso3, eop[i]?.allocated])),
  );

  const value = (v: number | null) => (v === null ? EMDASH : `${fmtNum(v, 0)}${unit ? ` ${unit}` : ''}`);
  const rows: TargetRowDatum[] = data.rows.map((row) => ({
    key: row.unitId,
    label: row.label,
    target: row.target,
    targetTip: t(targetTipKey(row)),
    segments: row.segments.map((seg) => ({
      year: seg.year,
      value: seg.value,
      tip: t('overview.targets.rows.tipYear', { year: seg.year }),
    })),
    achieved: row.achieved,
    aria: t('overview.targets.rows.aria', {
      name: row.label,
      target: value(row.target),
      achieved: value(row.achieved),
      years: row.segments.length === 0 ? EMDASH : row.segments.map((seg) => seg.year).join(', '),
    }),
  }));

  return (
    <div className="hp-chart ov-target" data-testid={`overview-target-${indicatorId}`}>
      <div
        className="ov-target-head"
        // The one place the apportioning is said in words, for a reader who hovers.
        title={apportioned ? t('overview.targets.apportionedTitle') : undefined}
      >
        <b>{indicator?.code ?? indicatorId.toUpperCase()}</b>
        <span>{t(`overview.targets.${indicatorId}`)}</span>
      </div>
      <TargetRows
        rows={rows}
        rowH={ovRowH(2)}
        labelGutter={116}
        labelSize={8}
        unit={unit}
        fmt={0}
        /* Four gridlines, not the set's five. The axis now runs to half a million rather
           than to a country's own achievement, so every tick is six characters wide, and at
           five of them across a 164 px plot the last two print over each other. */
        ticks={4}
        targetColor={TARGET_COLORS.target}
        actualsColor={TARGET_COLORS.achieved}
        onSelect={(_index, row) => onPick(row.key)}
        ariaLabel={t(`overview.targets.${indicatorId}`)}
      />
      {/*
        **The key the client asked back on 4 Oct 2026**, and the one standing exception to the
        2026-09-08 instruction that stripped the Target/Achieved legends off both panels:
        *"with a legend distinguishing targets and actuals."* Two bars of two colours inside
        one row is the whole distinction, and no row label can carry it.
      */}
      <div
        className="hp-mini-legend ov-legend"
        data-testid={`overview-target-legend-${indicatorId}`}
      >
        <span>
          <i style={{ background: TARGET_COLORS.target }} />
          {t('overview.targets.rows.legendTarget')}
        </span>
        <span>
          <i style={{ background: TARGET_COLORS.achieved }} />
          {t('overview.targets.rows.legendActuals')}
        </span>
      </div>
    </div>
  );
}

export default SplashLanding;
